import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useApp } from "../../contexts/AppContext";
import { toast } from "sonner";
import { Save, Search } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";

export default function AdminTexts() {
  const [texts, setTexts] = useState({});
  const [filter, setFilter] = useState("");
  const [draft, setDraft] = useState({});
  const { refreshTexts, settingsRaw, refreshSettings } = useApp();
  const [rulesWidth, setRulesWidth] = useState("");
  useEffect(() => { if (settingsRaw) setRulesWidth(String(settingsRaw.rules_content_width || 1024)); }, [settingsRaw]);
  const saveRulesWidth = async () => {
    const v = Math.max(600, Math.min(1600, parseInt(rulesWidth, 10) || 1024));
    try {
      await api.put("/admin/settings", { rules_content_width: v });
      await refreshSettings();
      setRulesWidth(String(v));
      toast.success(`Largura da página de Regras: ${v}px`);
    } catch (_) { toast.error("Erro ao guardar largura"); }
  };

  const load = async () => {
    const { data } = await api.get("/admin/texts");
    setTexts(data);
    setDraft(data);
  };
  useEffect(() => { load(); }, []);

  const setVal = (key, lang, v) => {
    setDraft({ ...draft, [key]: { ...draft[key], [lang]: v } });
  };

  const save = async (key) => {
    const entry = draft[key];
    try {
      await api.put("/admin/texts", { key, pt: entry.pt, en: entry.en, br: entry.br || "" });
      toast.success(`Guardado: ${key}`);
      await refreshTexts();
    } catch (e) {
      toast.error("Erro");
    }
  };

  const saveAll = async () => {
    const keys = Object.keys(draft);
    for (const k of keys) {
      if (!texts[k] || texts[k].pt !== draft[k].pt || texts[k].en !== draft[k].en || (texts[k].br || "") !== (draft[k].br || "")) {
        await api.put("/admin/texts", { key: k, pt: draft[k].pt, en: draft[k].en, br: draft[k].br || "" });
      }
    }
    toast.success("Todos os textos guardados");
    await refreshTexts();
    load();
  };

  const f = filter.trim().toLowerCase();
  const filtered = Object.keys(draft).filter((k) => {
    if (!f) return true;
    const entry = draft[k] || {};
    return (
      k.toLowerCase().includes(f) ||
      (entry.pt || "").toLowerCase().includes(f) ||
      (entry.en || "").toLowerCase().includes(f) ||
      (entry.br || "").toLowerCase().includes(f)
    );
  });

  return (
    <div data-testid="admin-texts-page">
      <button onClick={saveAll} data-testid="texts-save-floating"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF6B6B] text-white font-bold shadow-2xl hover:bg-[#ff5252] transition-colors">
        <Save size={18}/> Guardar tudo
      </button>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A]">Textos do Site (PT / EN / BR)</h1>
        <button onClick={saveAll} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B6B] text-white font-bold"><Save size={16}/> Guardar tudo</button>
      </div>

      <div className="mb-5 relative">
        <Search className="absolute left-3 top-3 text-[#0B1D3A]/40" size={16}/>
        <input
          placeholder="Procurar por chave ou conteúdo da mensagem (PT/EN)..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#0B1D3A]/15 bg-white"
          data-testid="texts-search-input"
        />
        <p className="mt-1.5 text-xs text-[#0B1D3A]/50" data-testid="texts-result-count">
          {filtered.length} {filtered.length === 1 ? "mensagem" : "mensagens"}{filter ? ` (de ${Object.keys(draft).length})` : ""}
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl bg-[#005F73]/[0.06] border border-[#005F73]/20 px-4 py-3">
        <label className="text-sm font-semibold text-[#0B1D3A]">Largura da página de Regras (px)
          <input type="number" min="600" max="1600" step="10" value={rulesWidth} onChange={(e) => setRulesWidth(e.target.value)}
            className="mt-1 block w-32 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 bg-white text-sm" data-testid="rules-width-input" />
        </label>
        <button onClick={saveRulesWidth} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#005F73] text-white font-bold text-sm" data-testid="rules-width-save">
          <Save size={15} /> Aplicar largura
        </button>
        <span className="text-xs text-[#0B1D3A]/50">Controla a largura do bloco de texto da página “Regras do Concurso”. (A4 ≈ 794px · ecrã largo ≈ 1100–1280px)</span>
      </div>

      <div className="space-y-3">
        {filtered.map((key) => (
          <div key={key} className="rounded-xl bg-white p-4 border border-[#0B1D3A]/5">
            <p className="text-xs font-mono text-[#0B1D3A]/50 mb-2">{key}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <span className="block text-[10px] font-semibold text-[#0B1D3A]/40 mb-1">🇵🇹 PT (Europa)</span>
                <RichTextEditor value={draft[key]?.pt || ""} onChange={(v) => setVal(key, "pt", v)} placeholder="PT (Europa)" testId={`text-pt-${key}`} />
              </div>
              <div>
                <span className="block text-[10px] font-semibold text-[#0B1D3A]/40 mb-1">🇧🇷 BR (Brasil)</span>
                <RichTextEditor value={draft[key]?.br || ""} onChange={(v) => setVal(key, "br", v)} placeholder="BR (Brasil)" testId={`text-br-${key}`} />
              </div>
              <div>
                <span className="block text-[10px] font-semibold text-[#0B1D3A]/40 mb-1">🇬🇧 EN</span>
                <RichTextEditor value={draft[key]?.en || ""} onChange={(v) => setVal(key, "en", v)} placeholder="EN" testId={`text-en-${key}`} />
              </div>
            </div>
            <div className="mt-2 text-right">
              <button onClick={() => save(key)} className="text-xs px-3 py-1.5 rounded-md bg-[#0B1D3A] text-white">Guardar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
